import { useMemo } from 'react';
import { ColDef, FirstDataRenderedEvent, ICellRendererParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { IHeaderParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { AgGridReact } from 'ag-grid-react';
import { IDashPwlJugBilling } from '@nkr/models/dash-pwl-jugbilling';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';

const rowClassRules = {
    'custom-row-border': () => true, // Apply the custom-row-border class to all rows
};

interface IProps {
    centerId: number,
    jugData: IDashPwlJugBilling[],
    isAdmin: boolean
}

const Grid = (props: IProps) => {
    const RowIndexRenderer = (props) => {
        const cellStyle = {
            paddingTop: '3px',
        };

        return (
            <div className="row-index-cell" style={cellStyle}>
                {props.node.rowIndex + 1}
            </div>
        );
    }

    const frameworkComponents = {
        rowIndexRenderer: RowIndexRenderer,
    };

    const cols: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rank</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            sortable: false,
            width: 85
        },
        {
            colId: 'registrationFullName',
            field: 'registrationFullName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let { registrationId, registrationFullName, centerId } = params.data;

                return <div><a href={`/center/${centerId}/donors/donor-intake/details/${registrationId}`}>{registrationFullName}</a> </div>
            },
            cellStyle: { textAlign: 'center' }
        },
        {
            colId: 'registrationDob',
            field: 'registrationDob',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Dob</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let { registrationDob } = params.data;

                return <div>{formatDateToISO(registrationDob)}</div>
            },
            cellStyle: { textAlign: 'center' },
            comparator: Utility.dateComparator
        },
        {
            colId: 'requestedDateTime',
            field: 'requestedDateTime',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Requested</SortableHeaderComponent>,
            cellStyle: { textAlign: 'center' },
            comparator: (date1: string, date2: string) => {
                if (date1 === null && date2 === null)
                    return 0;
    
                if (date1 === null)
                    return -1;
        
                if (date2 === null)
                    return 1;

                const parsedDate1 = new Date(date1);
                const parsedDate2 = new Date(date2);
            
                if (parsedDate1 < parsedDate2) return -1;
                if (parsedDate1 > parsedDate2) return 1;
            }
        }
    ];

    const formatDateToISO = (dateString: string) => {
        if (dateString == null) return '';
        if (dateString == '') return '';

        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Adding 1 to month since it's zero-based
        const day = String(date.getDate()).padStart(2, '0');

        return `${month}/${day}/${year}`;
    }

    const columnDefs = useMemo(() => cols, [])
    const rowData = useMemo(() => props.jugData, [props.jugData]);

    return (
        <GridWrapper>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={30}
                suppressCellSelection={true}
                suppressContextMenu={true}
                rowClassRules={rowClassRules}
                frameworkComponents={frameworkComponents}
                noRowsOverlayComponentFramework={() =>
                    <div>No data for the selected parameters...</div>}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    );
}


export default Grid;